"use client";

import { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Paper, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, TablePagination, Collapse, IconButton, CircularProgress,
  Divider, Grid, Tabs, Tab, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Checkbox, FormControlLabel, Stack, Tooltip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon   from "@mui/icons-material/KeyboardArrowUp";
import AddIcon                from "@mui/icons-material/Add";
import DeleteOutlineIcon      from "@mui/icons-material/DeleteOutline";
import EditIcon               from "@mui/icons-material/Edit";
import { axiosRequest } from "../../utility/axios";
import { formatISTDateTime, maskPhone } from "../../helper";
import { useSelector } from "react-redux";

const fmt = (v) => `${Number(v || 0).toLocaleString("en-IN")}`;

const WALLET_COLORS = {
  from_deposit: "#1976d2",
  from_winning: "#388e3c",
  from_promo:   "#9c27b0",
  from_loan:    "#f57c00",
  from_float:   "#e65100",
};

/* ─── Wallet breakdown cards ─── */
function WalletBreakdown({ data }) {
  if (!data) return null;
  const rows = [
    { key: "from_deposit", label: "Deposit Wallet",  color: WALLET_COLORS.from_deposit },
    { key: "from_winning", label: "Winning Wallet",  color: WALLET_COLORS.from_winning },
    { key: "from_promo",   label: "Promo Wallet",    color: WALLET_COLORS.from_promo   },
    { key: "from_loan",    label: "Loan Wallet",     color: WALLET_COLORS.from_loan    },
    { key: "from_float",   label: "Float Exchange",  color: WALLET_COLORS.from_float   },
  ];
  const total = data.total || 0;
  return (
    <Box>
      <Typography variant="body2" fontWeight={700} mb={1.5}>
        Chip Issue — Wallet Breakdown &nbsp;
        <Typography component="span" color="primary.main" fontWeight={700}>{fmt(total)}</Typography>
        <Typography component="span" color="text.secondary" variant="caption"> ({data.count || 0} issues)</Typography>
      </Typography>
      <Grid container spacing={1.5}>
        {rows.map((r) => (
          <Grid item xs={6} sm={3} key={r.key}>
            <Paper sx={{ p: 1.5, borderLeft: `4px solid ${r.color}`, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">{r.label}</Typography>
              <Typography fontWeight={700} sx={{ color: r.color }}>{fmt(data[r.key])}</Typography>
              {total > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {(((data[r.key] || 0) / total) * 100).toFixed(1)}%
                </Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

/* ─── User amount table ─── */
function UserTable({ rows, amountKey, label, totalLabel }) {
  const total = rows.reduce((s, r) => s + (r[amountKey] || 0), 0);
  return (
    <Box>
      <Typography variant="body2" fontWeight={700} mb={1}>
        {label} &nbsp;
        <Typography component="span" color="primary.main" fontWeight={700}>{fmt(total)}</Typography>
        <Typography component="span" color="text.secondary" variant="caption"> ({rows.length} players)</Typography>
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No records in this session.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: "#f4f6f8" }}>
              <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{totalLabel || "Amount"}</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Txns</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i} hover>
                <TableCell>{i + 1}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{r.name || "—"}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.playerId}</Typography>
                </TableCell>
                <TableCell>{maskPhone(r.phone) || "—"}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: "primary.main" }}>
                  {fmt(r[amountKey])}
                </TableCell>
                <TableCell align="center">{r.count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

/* ─── Loan breakdown ─── */
function LoanBreakdown({ utilized, repay }) {
  const utilizedTotal = utilized?.total || 0;
  const repayTotal    = repay?.total    || 0;

  const utilizedUsers = utilized?.users || [];
  const repayUsers    = repay?.users    || [];

  // merge per-user
  const userMap = {};
  utilizedUsers.forEach(u => {
    userMap[u._id] = { name: u.name, phone: u.phone, playerId: u.playerId, utilized: u.total, utilized_count: u.count, repay: 0, repay_count: 0 };
  });
  repayUsers.forEach(u => {
    if (userMap[u._id]) {
      userMap[u._id].repay       = u.total;
      userMap[u._id].repay_count = u.count;
    } else {
      userMap[u._id] = { name: u.name, phone: u.phone, playerId: u.playerId, utilized: 0, utilized_count: 0, repay: u.total, repay_count: u.count };
    }
  });
  const rows = Object.values(userMap).sort((a, b) => b.utilized - a.utilized);
  // Sum per-player outstanding — avoids zeroing out when repaid > utilized across players
  const outstanding = rows.reduce((sum, r) => sum + Math.max(0, r.utilized - r.repay), 0);

  return (
    <Box>
      <Grid container spacing={1.5} mb={2}>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 1.5, borderLeft: "4px solid #f57c00", borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">Loan Utilized</Typography>
            <Typography fontWeight={700} sx={{ color: "#f57c00" }}>{fmt(utilizedTotal)}</Typography>
            <Typography variant="caption" color="text.secondary">{utilizedUsers.length} players</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 1.5, borderLeft: "4px solid #388e3c", borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">Loan Repaid</Typography>
            <Typography fontWeight={700} sx={{ color: "#388e3c" }}>{fmt(repayTotal)}</Typography>
            <Typography variant="caption" color="text.secondary">{repayUsers.length} players</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 1.5, borderLeft: "4px solid #d32f2f", borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">Net Outstanding</Typography>
            <Typography fontWeight={700} sx={{ color: outstanding > 0 ? "#d32f2f" : "#388e3c" }}>{fmt(outstanding)}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No loan activity in this session.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: "#f4f6f8" }}>
              <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "#f57c00" }}>Loan Utilized</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "#388e3c" }}>Loan Repaid</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "#d32f2f" }}>Outstanding</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => {
              const net = Math.max(0, r.utilized - r.repay);
              return (
                <TableRow key={i} hover>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{r.name || "—"}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.playerId}</Typography>
                  </TableCell>
                  <TableCell>{maskPhone(r.phone) || "—"}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "#f57c00" }}>{fmt(r.utilized)}</TableCell>
                  <TableCell align="right" sx={{ color: "#388e3c" }}>{fmt(r.repay)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: net > 0 ? 700 : 400, color: net > 0 ? "#d32f2f" : "text.secondary" }}>{fmt(net)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

/* ─── Float exchange breakdown ─── */
const FLOAT_SOURCE_LABEL = {
  CASHOUT: "Chip Cash-Out", CHIP_RETURN: "Chips Returned",
  DEPOSIT_CASH: "Deposit (Cash)", DEPOSIT_BANK: "Deposit (Bank)", DEPOSIT_OTHER: "Deposit (Other)",
};

function FloatBreakdown({ data }) {
  if (!data) return null;
  const { issued = {}, repaid = {}, outstanding = {}, carried_in = {}, players = [] } = data;
  const cards = [
    { label: "Float Issued",       value: issued.total,      sub: `${issued.count || 0} floats`,      color: "#e65100" },
    { label: "Float Repaid",       value: repaid.total,      sub: `${repaid.count || 0} repayments`,  color: "#388e3c" },
    { label: "Carried In",         value: carried_in.total,  sub: `${carried_in.count || 0} players`, color: "#546e7a" },
    { label: "Outstanding at End", value: outstanding.total, sub: `${outstanding.count || 0} players`, color: "#d32f2f" },
  ];
  return (
    <Box>
      <Grid container spacing={1.5} mb={2}>
        {cards.map((c) => (
          <Grid item xs={6} sm={3} key={c.label}>
            <Paper sx={{ p: 1.5, borderLeft: `4px solid ${c.color}`, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">{c.label}</Typography>
              <Typography fontWeight={700} sx={{ color: c.color }}>{fmt(c.value)}</Typography>
              <Typography variant="caption" color="text.secondary">{c.sub}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {Object.keys(repaid.by_source || {}).length > 0 && (
        <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
          {Object.entries(repaid.by_source).map(([k, v]) => (
            <Chip key={k} size="small" variant="outlined" label={`${FLOAT_SOURCE_LABEL[k] || k}: ${fmt(v)}`} />
          ))}
        </Stack>
      )}

      {players.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No float activity in this session.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: "#f4f6f8" }}>
              <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "#e65100" }}>Issued</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "#388e3c" }}>Repaid</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "#d32f2f" }}>Outstanding</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {players.map((r, i) => (
              <TableRow key={String(r.user_id)} hover>
                <TableCell>{i + 1}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{r.name || "—"}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.player_id}</Typography>
                </TableCell>
                <TableCell>{maskPhone(r.phone) || "—"}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: "#e65100" }}>{fmt(r.issued)}</TableCell>
                <TableCell align="right" sx={{ color: "#388e3c" }}>{fmt(r.repaid)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: r.outstanding > 0 ? 700 : 400, color: r.outstanding > 0 ? "#d32f2f" : "text.secondary" }}>{fmt(r.outstanding)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

/* ─── Chip cashout summary ─── */
function ChipCashoutBreakdown({ data }) {
  if (!data) return null;
  const { users = [], total = 0, float_repay = 0, loan_repay = 0, to_winning = 0 } = data;
  return (
    <Box>
      <Typography variant="body2" fontWeight={700} mb={1.5}>
        Chip Cashout &nbsp;
        <Typography component="span" color="primary.main" fontWeight={700}>{fmt(total)}</Typography>
        <Typography component="span" color="text.secondary" variant="caption"> ({users.length} players)</Typography>
      </Typography>
      <Grid container spacing={1.5} mb={2}>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 1.5, borderLeft: "4px solid #388e3c", borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">To Winning Wallet</Typography>
            <Typography fontWeight={700} sx={{ color: "#388e3c" }}>{fmt(to_winning)}</Typography>
            {total > 0 && <Typography variant="caption" color="text.secondary">{((to_winning / total) * 100).toFixed(1)}%</Typography>}
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 1.5, borderLeft: "4px solid #f57c00", borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">Loan Repay</Typography>
            <Typography fontWeight={700} sx={{ color: "#f57c00" }}>{fmt(loan_repay)}</Typography>
            {total > 0 && <Typography variant="caption" color="text.secondary">{((loan_repay / total) * 100).toFixed(1)}%</Typography>}
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 1.5, borderLeft: "4px solid #e65100", borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">Float Repay</Typography>
            <Typography fontWeight={700} sx={{ color: "#e65100" }}>{fmt(float_repay)}</Typography>
            {total > 0 && <Typography variant="caption" color="text.secondary">{((float_repay / total) * 100).toFixed(1)}%</Typography>}
          </Paper>
        </Grid>
      </Grid>
      {users.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: "#f4f6f8" }}>
              <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Cashout</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Float Repay</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Loan Repay</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>To Winning</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Txns</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((r, i) => (
              <TableRow key={i} hover>
                <TableCell>{i + 1}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{r.user_name || "—"}</Typography>
                  <Typography variant="caption" color="text.secondary">{r._id}</Typography>
                </TableCell>
                <TableCell>{maskPhone(r.phone) || "—"}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: "primary.main" }}>{fmt(r.total)}</TableCell>
                <TableCell align="right" sx={{ color: "#e65100" }}>{fmt(r.float_repay)}</TableCell>
                <TableCell align="right" sx={{ color: "#f57c00" }}>{fmt(r.loan_repay)}</TableCell>
                <TableCell align="right" sx={{ color: "#388e3c" }}>{fmt(r.to_winning)}</TableCell>
                <TableCell align="center">{r.count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

/* ─── Add shifts to existing session dialog ─── */
function AddShiftsDialog({ open, onClose, token, session, onUpdated }) {
  const [unassigned,    setUnassigned]    = useState([]);
  const [selected,      setSelected]      = useState([]);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [submitting,    setSubmitting]    = useState(false);
  const [error,         setError]         = useState("");

  useEffect(() => {
    if (!open) return;
    setSelected([]); setError("");
    setLoadingShifts(true);
    axiosRequest("GET", "/sessions/unassigned-shifts", token)
      .then((res) => setUnassigned(res?.data || []))
      .catch(console.error)
      .finally(() => setLoadingShifts(false));
  }, [open]);

  const toggle = (id) =>
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const handleSubmit = async () => {
    if (!selected.length) { setError("Select at least one session"); return; }
    setSubmitting(true);
    try {
      const res = await axiosRequest("POST", `/sessions/${session._id}/shifts`, token, { shift_ids: selected });
      if (res?.data?.status === false) { setError(res.data.message); return; }
      onUpdated();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Cashier Ledger Sessions — <Typography component="span" fontWeight={700}>{session.name}</Typography></DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          <Box>
            <Typography variant="body2" fontWeight={600} mb={1}>
              Select unassigned closed sessions:
            </Typography>
            {loadingShifts ? (
              <CircularProgress size={20} />
            ) : unassigned.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No unassigned closed sessions available.
              </Typography>
            ) : (
              <Paper variant="outlined" sx={{ maxHeight: 280, overflowY: "auto", p: 1 }}>
                {unassigned.map((s) => (
                  <FormControlLabel
                    key={s._id}
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.includes(s._id)}
                        onChange={() => toggle(s._id)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{formatISTDateTime(s.date_time)}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.email}</Typography>
                      </Box>
                    }
                    sx={{ display: "flex", mb: 0.5 }}
                  />
                ))}
              </Paper>
            )}
          </Box>
          {error && <Typography color="error" variant="body2">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting || !selected.length}>
          {submitting ? <CircularProgress size={18} /> : `Add ${selected.length ? `(${selected.length})` : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ─── Single session row ─── */
function SessionRow({ session, token, onRefresh }) {
  const [open,           setOpen]           = useState(false);
  const [analytics,      setAnalytics]      = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [tab,            setTab]            = useState(0);
  const [addShiftsOpen,  setAddShiftsOpen]  = useState(false);

  const loadAnalytics = async () => {
    if (analytics || loading) return;
    setLoading(true);
    try {
      const res = await axiosRequest("GET", `/sessions/${session._id}/analytics`, token);
      setAnalytics(res?.data || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleExpand = () => {
    setOpen((p) => !p);
    if (!open) loadAnalytics();
  };

  const handleRemoveShift = async (shiftId) => {
    await axiosRequest("DELETE", `/sessions/${session._id}/shifts/${shiftId}`, token);
    setAnalytics(null);
    onRefresh();
  };

  return (
    <>
      <TableRow hover sx={{ "& > *": { borderBottom: "unset" }, cursor: "pointer" }} onClick={handleExpand}>
        <TableCell>
          <IconButton size="small">{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>
        </TableCell>
        <TableCell>
          <Typography fontWeight={600}>{session.name}</Typography>
          {session.remarks && <Typography variant="caption" color="text.secondary">{session.remarks}</Typography>}
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
            {(session.shift_ids || []).map((s) => (
              <Chip
                key={s._id}
                size="small"
                label={formatISTDateTime(s.date_time)}
                color={s.shift_status ? "default" : "success"}
                variant="outlined"
                onClick={(e) => e.stopPropagation()}
                onDelete={(e) => { e.stopPropagation(); handleRemoveShift(s._id); }}
                deleteIcon={<DeleteOutlineIcon fontSize="small" />}
              />
            ))}
            {!session.shift_ids?.length && (
              <Typography variant="caption" color="text.secondary">No shifts assigned</Typography>
            )}
            <Tooltip title="Add cashier ledger sessions">
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => { e.stopPropagation(); setAddShiftsOpen(true); }}
                sx={{ ml: 0.5 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
        <TableCell align="center">{session.shift_ids?.length || 0}</TableCell>
        <TableCell>
          <Chip label={session.status} size="small" color={session.status === "OPEN" ? "success" : "default"} />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={5} sx={{ p: 0, background: "#fafbfc" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box p={3}>
              {loading && <CircularProgress size={24} />}
              {analytics && !loading && (
                <>
                  {analytics.period && (
                    <Typography variant="caption" color="text.secondary" mb={1} display="block">
                      Period: {formatISTDateTime(analytics.period.start)} → {formatISTDateTime(analytics.period.end)}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} mb={2}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Unique Depositors: ${analytics.unique_depositor_count ?? 0}`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Unique Chip Issue Users: ${analytics.unique_chip_issue_user_count ?? 0}`}
                    />
                  </Stack>
                  <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
                    <Tab label="Deposit Cashback" />
                    <Tab label="Promo Credit" />
                    <Tab label="Chip Issue (Wallet Wise)" />
                    <Tab label="Chip Cashout" />
                    <Tab label="Loan" />
                    <Tab label="Float Exchange" />
                  </Tabs>
                  {tab === 0 && (
                    <UserTable rows={analytics.deposit_cashback?.users || []} amountKey="total_cashback" label="Deposit Cashback" totalLabel="Cashback" />
                  )}
                  {tab === 1 && (
                    <UserTable rows={analytics.promo_credit?.users || []} amountKey="total_promo" label="Promo Credit" totalLabel="Promo" />
                  )}
                  {tab === 2 && (
                    <WalletBreakdown data={analytics.chip_issue_wallets} />
                  )}
                  {tab === 3 && (
                    <ChipCashoutBreakdown data={analytics.chip_cashout} />
                  )}
                  {tab === 4 && (
                    <LoanBreakdown utilized={analytics.loan_utilized} repay={analytics.loan_repay} />
                  )}
                  {tab === 5 && (
                    <FloatBreakdown data={analytics.float_exchange} />
                  )}
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <AddShiftsDialog
        open={addShiftsOpen}
        onClose={() => setAddShiftsOpen(false)}
        token={token}
        session={session}
        onUpdated={() => { setAnalytics(null); onRefresh(); }}
      />
    </>
  );
}

/* ─── Create session dialog ─── */
function CreateSessionDialog({ open, onClose, token, onCreated }) {
  const [name,           setName]           = useState("");
  const [remarks,        setRemarks]        = useState("");
  const [unassigned,     setUnassigned]     = useState([]);
  const [selected,       setSelected]       = useState([]);
  const [loadingShifts,  setLoadingShifts]  = useState(false);
  const [submitting,     setSubmitting]     = useState(false);
  const [error,          setError]          = useState("");

  useEffect(() => {
    if (!open) return;
    setName(""); setRemarks(""); setSelected([]); setError("");
    setLoadingShifts(true);
    axiosRequest("GET", "/sessions/unassigned-shifts", token)
      .then((res) => setUnassigned(res?.data || []))
      .catch(console.error)
      .finally(() => setLoadingShifts(false));
  }, [open]);

  const toggle = (id) =>
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const handleSubmit = async () => {
    if (!name.trim()) { setError("Session name is required"); return; }
    setSubmitting(true);
    try {
      const res = await axiosRequest("POST", "/sessions", token, { name: name.trim(), remarks, shift_ids: selected });
      if (res?.data?.status === false) { setError(res.data.message); return; }
      onCreated();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Session</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          <TextField
            label="Session Name" fullWidth size="small" required
            value={name} onChange={(e) => setName(e.target.value)}
            error={!!error && !name.trim()} helperText={!name.trim() && error ? error : ""}
          />
          <TextField
            label="Remarks (optional)" fullWidth size="small"
            value={remarks} onChange={(e) => setRemarks(e.target.value)}
          />

          <Box>
            <Typography variant="body2" fontWeight={600} mb={1}>
              Select Cashier Ledger Sessions to include:
            </Typography>
            {loadingShifts ? (
              <CircularProgress size={20} />
            ) : unassigned.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No unassigned closed sessions available.
              </Typography>
            ) : (
              <Paper variant="outlined" sx={{ maxHeight: 260, overflowY: "auto", p: 1 }}>
                {unassigned.map((s) => (
                  <FormControlLabel
                    key={s._id}
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.includes(s._id)}
                        onChange={() => toggle(s._id)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{formatISTDateTime(s.date_time)}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.email}</Typography>
                      </Box>
                    }
                    sx={{ display: "flex", mb: 0.5 }}
                  />
                ))}
              </Paper>
            )}
          </Box>

          {error && name.trim() && (
            <Typography color="error" variant="body2">{error}</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? <CircularProgress size={18} /> : "Create Session"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ─── Main page ─── */
export default function SessionManager() {
  const [sessions,    setSessions]    = useState([]);
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total,       setTotal]       = useState(0);
  const [loading,     setLoading]     = useState(false);
  const [createOpen,  setCreateOpen]  = useState(false);

  const token = useSelector((s) => s.loginSlice.token);

  const fetchSessions = useCallback(async (pg = page, rpp = rowsPerPage) => {
    setLoading(true);
    try {
      const res  = await axiosRequest("GET", `/sessions?page=${pg + 1}&size=${rpp}`, token);
      const data = res?.data || {};
      setSessions(data?.sessions || []);
      setTotal(data?.pagination?.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, rowsPerPage, token]);

  useEffect(() => { fetchSessions(); }, [page, rowsPerPage]);

  return (
    <Box sx={{ width: "100%", marginTop: { xs: "55px", md: "95px" } }}>
    <Box p={3} sx={{ padding: { xs: "16px", md: "32px" } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Session Manager</Typography>
          <Typography color="text.secondary">
            Group cashier ledger sessions — deposit cashback, promo credit &amp; chip wallet breakdown
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} sx={{ borderRadius: 3, height: 45 }}>
          Create Session
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow sx={{ background: "#f4f6f8" }}>
                  <TableCell width={48} />
                  <TableCell sx={{ fontWeight: 700 }}>Session Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Cashier Ledger Sessions</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Shifts</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary" py={3}>
                        No sessions yet. Click "Create Session" to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sessions.map((s) => (
                    <SessionRow key={s._id} session={s} token={token} onRefresh={() => fetchSessions()} />
                  ))
                )}
              </TableBody>
            </Table>
            <Divider />
            <TablePagination
              component="div"
              count={total}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}
      </Paper>

      <CreateSessionDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        token={token}
        onCreated={() => fetchSessions()}
      />
    </Box>
    </Box>
  );
}
